import type { Formule } from '@repere/core';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../auth/session';

export type RegimeAcces = 'gratuit' | 'abonne' | 'couvert' | 'professionnel';

export interface Droits {
  acces: RegimeAcces;
  ecriture: string[];
  /**
   * La formule affichable — `link`, `solo`, `duo` ou `pro`. Le serveur la calcule, et c'est
   * lui qui garantit qu'un compte couvert renvoie `solo` : la recomposer ici exposerait
   * l'écran à dire « Duo » à qui ne paie pas, donc à lui apprendre que l'autre paie.
   *
   * Facultative dans le type : une réponse d'API plus ancienne, ou un test qui ne la pose
   * pas, ne doit pas faire disparaître le badge de l'en-tête.
   */
  formule?: Formule;
  /** Y a-t-il un autre parent à couvrir dès maintenant ? Faux en mode parent seul. */
  peutCouvrirCoparent: boolean;
}

/**
 * Le régime du compte, lu une seule fois pour tout l'écran.
 *
 * Le badge de l'en-tête, la couronne du rail et le rappel d'abonnement posent tous la même
 * question. Chacun avait sa requête et sa propre idée de la forme de la réponse ; une clé
 * partagée et un type unique évitent qu'ils se contredisent à l'écran le temps qu'une des
 * trois réponses arrive.
 *
 * `staleTime` long : le régime ne change qu'à un paiement ou à l'ouverture d'un accès
 * professionnel, jamais au fil de la navigation.
 */
export function useRegime() {
  const { client, profil } = useSession();

  return useQuery({
    queryKey: ['droits'],
    enabled: profil !== null,
    staleTime: 5 * 60_000,
    queryFn: () => client.request<Droits>('/billing/entitlements'),
  });
}

/**
 * Vrai **seulement** quand la réponse est arrivée et dit « gratuit ».
 *
 * Le doute compte comme « pas gratuit », et c'est délibéré : tant qu'on ne sait pas, on
 * n'affiche pas le rappel d'abonnement. Se tromper dans ce sens ne coûte qu'un rappel
 * différé d'une seconde ; se tromper dans l'autre propose de souscrire à quelqu'un qui
 * paie déjà.
 */
export function useVersionGratuite(): boolean {
  const droits = useRegime();
  return droits.data?.acces === 'gratuit';
}

/**
 * L'écriture est-elle ouverte, et à quel titre ?
 *
 * `couvert` compte comme ouverte : le parent écrit exactement comme un abonné. La
 * distinction ne sert qu'à un écran — celui qui explique d'où vient cet accès et ce qui
 * arrive s'il s'arrête.
 */
export function useEcritureOuverte(): boolean {
  const droits = useRegime();
  return droits.data?.acces === 'abonne' || droits.data?.acces === 'couvert';
}
